import {
	User as FirebaseUser,
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signInWithPopup,
} from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { firebaseAuth } from "../config/firebase.ts";
import { PUBLICIS_LOREAL_ORG_ID, PUBLICIS_ORG_ID } from "../constants/OrgConstants.ts";
import { USER_ORGANIZATION_ROLES, USER_ORGANIZATION_STATUS } from "../constants/UserConstants.ts";
import { createFirebaseUser, getUserWithUID } from "../helpers/firebase.ts";
import { UserOrganizationFlat } from "../models/organization.ts";
import useErrorToast from "./useErrorToast.tsx";
import { useAppStore } from "./useStores.tsx";

export const useAuthService = () => {
	const EXCLUSION_EXCEPTIONS_LIST = ["auth/popup-closed-by-user", "auth/cancelled-popup-request"];
	const errorToast = useErrorToast();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { authStore } = useAppStore();
	const [errorMessage, setErrorMessage] = useState<boolean | string>(false);
	const [loading, setIsLoading] = useState<boolean>(false);

	async function createFirebaseUserAndUpdateStore(
		userToRegister: FirebaseUser,
		getInfo: boolean,
		username?: string,
		company?: string,
		role?: string,
		userOrganizations?: UserOrganizationFlat[],
	) {
		await createFirebaseUser(userToRegister, getInfo, username, company, role, userOrganizations);

		// Retry getting user if it doesn't exist immediately
		let updatedUser = await getUserWithUID(userToRegister.uid);
		if (!updatedUser) {
			await new Promise((resolve) => setTimeout(resolve, 500));
			updatedUser = await getUserWithUID(userToRegister.uid);
		}

		await authStore.setUser(updatedUser);
		await authStore.setPostRegistrationConfigs();
		return updatedUser;
	}

	const authenticateWithPopup = async (provider: any) => {
		try {
			const result = await signInWithPopup(firebaseAuth, provider);
			const userToRegister = result.user;

			if (!userToRegister) {
				errorToast.showError();
				return Promise.reject(t("somethingWentWrong"));
			}

			const userObject = await getUserWithUID(userToRegister.uid);

			if (userObject) {
				// Login
				return Promise.resolve(userObject?.getInfo ?? true);
			} else {
				// Signup
				try {
					const updatedUser = await createFirebaseUserAndUpdateStore(
						userToRegister,
						false,
						undefined,
						"Publicis",
						undefined,
						[
							{
								org_uid: PUBLICIS_ORG_ID,
								role: USER_ORGANIZATION_ROLES.USER,
								status: USER_ORGANIZATION_STATUS.ACTIVE,
							},
						],
					);
					return Promise.resolve(updatedUser?.getInfo ?? true);
				} catch (error) {
					errorToast.showError(t("couldntRegisterFirebaseUser"));
					return Promise.reject(error);
				}
			}
		} catch (error: any) {
			if (error.code === "auth/account-exists-with-different-credential") {
				errorToast.showError(t("userExistsWithDifferentCredentials"));
				return Promise.reject(t("userExistsWithDifferentCredentials"));
			} else if (!EXCLUSION_EXCEPTIONS_LIST.includes(error.code)) {
				errorToast.showError();
				return Promise.reject(error);
			}
		}
	};

	const loginWithEmail = async (
		email: string,
		password: string,
		continueUrl?: string,
		state?: any,
		onSuccessCallback?: () => void,
	) => {
		setIsLoading(true);
		try {
			await signInWithEmailAndPassword(firebaseAuth, email, password);
			onSuccessCallback?.();
			if (continueUrl) {
				navigate(continueUrl, { state: state });
			}
		} catch (error: any) {
			setErrorMessage(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const registerWithEmail = async (
		username: string,
		email: string,
		password: string,
		company?: string,
		role?: string,
	) => {
		try {
			setIsLoading(true);
			const result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
			const userToRegister = await result.user;
			if (!userToRegister) {
				errorToast.showError();
				return Promise.reject(t("somethingWentWrong"));
			}
			const updatedUser = await createFirebaseUserAndUpdateStore(userToRegister, true, username, company, role, [
				{
					org_uid: PUBLICIS_LOREAL_ORG_ID,
					role: USER_ORGANIZATION_ROLES.USER,
					status: USER_ORGANIZATION_STATUS.ACTIVE,
				},
			]);
			setIsLoading(false);
			return Promise.resolve(updatedUser?.getInfo ?? true);
		} catch (error: any) {
			setIsLoading(false);
			setErrorMessage(error.message);
			return Promise.reject(error);
		}
	};

	return {
		authenticateWithPopup,
		loginWithEmail,
		errorMessage,
		setErrorMessage,
		loading,
		registerWithEmail,
	};
};
